import type { ActionSetKey } from "../blueprint/types.js";
import { normalizeWhiteSpaceValue } from "../blueprint/index.js";

export type BarLoaderMode = "full" | "small" | "bar";
export type BarLoaderUnit = "kg" | "lbs";
export type BarLoaderLabelOrientation = "horizontal" | "vertical";
export type BarLoaderTargetPart = "disk" | "label";
export type BarLoaderCompactWidthMode = "fixed" | "variable";

export interface BarLoaderDiskDefinition {
  weight: number;
  defaultColor: string;
  optional?: boolean;
}

export interface BarLoaderBlockStyle {
  visibility?: boolean;
  width?: number;
  height?: number;
  margin?: string;
  marginTop?: string;
  marginRight?: string;
  marginBottom?: string;
  marginLeft?: string;
  padding?: string;
  paddingTop?: string;
  paddingRight?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  backgroundColor?: string;
  opacity?: number;
  transform?: string;
  border?: string;
  borderTop?: string;
  borderRight?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRadius?: string;
  borderTopLeftRadius?: string;
  borderTopRightRadius?: string;
  borderBottomRightRadius?: string;
  borderBottomLeftRadius?: string;
  color?: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  textAlign?: "left" | "center" | "right";
  textTransform?: "none" | "capitalize" | "uppercase" | "lowercase";
  whiteSpace?: string;
  textDecorationLine?: string;
  textDecorationColor?: string;
  textDecorationStyle?: string;
  textDecorationThickness?: number;
}

export interface BarLoaderDiskStyleEntry {
  labelText?: string;
  label: BarLoaderBlockStyle;
  disk: BarLoaderBlockStyle;
}

export interface BarLoaderTargetRef {
  component?: "barLoader";
  weight: number | "all";
  part: BarLoaderTargetPart;
}

export interface BarLoaderTargetPatch {
  target: BarLoaderTargetRef;
  key: ActionSetKey | string;
  value: unknown;
}

/** Horizontal alignment of the disk row in compact modes (`small`, `bar`). */
export type BarLoaderCompactDiskAlign = "start" | "center" | "end";

export interface BarLoaderProps {
  mode?: BarLoaderMode;
  unit?: BarLoaderUnit;
  totalWeight?: number;
  /** Base bar weight used by plate loading computation (defaults to 25kg / 55lbs). */
  barWeight?: number;
  /** Collar weight for one side. Two collars are always included in total load computation. */
  collarsWeight?: number;
  optionalDisks?: {
    kg50?: boolean;
    lbs100?: boolean;
  };
  labelOrientation?: BarLoaderLabelOrientation;
  /** In `small` / `bar` modes: flex alignment of disk blocks (default `center`). */
  compactDiskAlign?: BarLoaderCompactDiskAlign;
  /** In `small` / `bar` modes: fixed width keeps capacity footprint, variable width follows displayed disks. */
  compactWidthMode?: BarLoaderCompactWidthMode;
  selectedDiskWeight?: number | "all";
  layoutStyle?: BarLoaderBlockStyle;
  diskStyles?: Record<string, BarLoaderDiskStyleEntry>;
  diskDefaultStyles?: Record<string, BarLoaderDiskStyleEntry>;
  barLoaderTargetPatches?: BarLoaderTargetPatch[];
}

export const BAR_WEIGHT_BY_UNIT: Record<BarLoaderUnit, number> = {
  kg: 25,
  lbs: 55,
};

export const DEFAULT_COLLARS_WEIGHT = 2.5;

export function getBarWeight(
  unit: BarLoaderUnit,
  barWeight: number | undefined,
): number {
  if (typeof barWeight === "number" && Number.isFinite(barWeight)) {
    return Math.max(0, barWeight);
  }
  return BAR_WEIGHT_BY_UNIT[unit];
}

export function getCollarsWeight(collarsWeight: number | undefined): number {
  if (typeof collarsWeight === "number" && Number.isFinite(collarsWeight)) {
    return Math.max(0, collarsWeight);
  }
  return DEFAULT_COLLARS_WEIGHT;
}

/** Max modeled total bar load for capacity sizing in `bar` mode (one side pairs included in algorithm). */
export const BAR_LOADER_CAPACITY_TOTAL_KG = 1000;
export const BAR_LOADER_CAPACITY_TOTAL_LBS = 2200;

/** Max disk columns modeled for footprint / resize distribution in `small` mode. */
export const BAR_LOADER_SMALL_MODE_MAX_COLUMNS_KG = 6;
export const BAR_LOADER_SMALL_MODE_MAX_COLUMNS_LBS = 7;

export const BAR_LOADER_DISKS_BY_UNIT: Record<
  BarLoaderUnit,
  BarLoaderDiskDefinition[]
> = {
  kg: [
    { weight: 50, defaultColor: "#8B0000", optional: true },
    { weight: 25, defaultColor: "#FF3B30" },
    { weight: 20, defaultColor: "#007AFF" },
    { weight: 15, defaultColor: "#FFD60A" },
    { weight: 10, defaultColor: "#34C759" },
    { weight: 5, defaultColor: "#F5F5F5" },
    { weight: 2.5, defaultColor: "#1C1C1E" },
    { weight: 1.25, defaultColor: "#8E8E93" },
    { weight: 1, defaultColor: "#B0B0B0" },
    { weight: 0.5, defaultColor: "#C0C0C0" },
    { weight: 0.25, defaultColor: "#D0D0D0" },
  ],
  lbs: [
    { weight: 100, defaultColor: "#8B0000", optional: true },
    { weight: 55, defaultColor: "#FF3B30" },
    { weight: 45, defaultColor: "#007AFF" },
    { weight: 35, defaultColor: "#FFD60A" },
    { weight: 25, defaultColor: "#34C759" },
    { weight: 10, defaultColor: "#F5F5F5" },
    { weight: 5, defaultColor: "#1C1C1E" },
    { weight: 2.5, defaultColor: "#8E8E93" },
    { weight: 1.25, defaultColor: "#B0B0B0" },
    { weight: 0.5, defaultColor: "#C0C0C0" },
    { weight: 0.25, defaultColor: "#D0D0D0" },
  ],
};

export const DEFAULT_BAR_LOADER_LAYOUT_STYLE: BarLoaderBlockStyle = {
  visibility: true,
  backgroundColor: "#ffffff",
  opacity: 1,
  transform: "",
  borderRadius: "0px",
};

export const DEFAULT_BAR_LOADER_LABEL_STYLE: BarLoaderBlockStyle = {
  visibility: true,
  width: 60,
  height: 56,
  backgroundColor: "#0056B3",
  opacity: 1,
  borderRadius: "5px",
  color: "#ffffff",
  fontSize: 28,
  fontWeight: "700",
  textAlign: "center",
  whiteSpace: "pre",
};

export const DEFAULT_BAR_LOADER_DISK_STYLE: BarLoaderBlockStyle = {
  visibility: true,
  width: 60,
  height: 112,
  opacity: 1,
  borderRadius: "5px",
  fontSize: 40,
  fontWeight: "700",
  textAlign: "center",
  whiteSpace: "pre",
};

export function getEnabledDisks(
  unit: BarLoaderUnit,
  optionalDisks: BarLoaderProps["optionalDisks"],
): BarLoaderDiskDefinition[] {
  return BAR_LOADER_DISKS_BY_UNIT[unit].filter((disk) => {
    if (!disk.optional) return true;
    if (unit === "kg") return Boolean(optionalDisks?.kg50);
    return Boolean(optionalDisks?.lbs100);
  });
}

export function calculateLoadedDisks(
  totalWeight: number,
  unit: BarLoaderUnit,
  optionalDisks: BarLoaderProps["optionalDisks"],
  barWeight?: number,
  collarsWeight?: number,
): number[] {
  const effectiveBarWeight = getBarWeight(unit, barWeight);
  const effectiveCollarsWeight = getCollarsWeight(collarsWeight);
  const definitions = getEnabledDisks(unit, optionalDisks);
  let remaining = Math.max(
    0,
    totalWeight - effectiveBarWeight - effectiveCollarsWeight * 2,
  );
  const result: number[] = [];

  for (const disk of definitions) {
    while (remaining >= disk.weight * 2 - 1e-9) {
      result.push(disk.weight);
      remaining -= disk.weight * 2;
    }
  }
  return result;
}

export function getBarLoaderCapacityTotalWeight(unit: BarLoaderUnit): number {
  return unit === "kg"
    ? BAR_LOADER_CAPACITY_TOTAL_KG
    : BAR_LOADER_CAPACITY_TOTAL_LBS;
}

export function getCompactModeMaxDiskColumns(unit: BarLoaderUnit): number {
  return unit === "kg"
    ? BAR_LOADER_SMALL_MODE_MAX_COLUMNS_KG
    : BAR_LOADER_SMALL_MODE_MAX_COLUMNS_LBS;
}

/**
 * Disk list at max modeled total ({@link BAR_LOADER_CAPACITY_TOTAL_KG} / `_LBS`), same algorithm as runtime load.
 */
export function calculateLoadedDisksAtCapacity(
  unit: BarLoaderUnit,
  optionalDisks: BarLoaderProps["optionalDisks"],
  barWeight?: number,
  collarsWeight?: number,
): number[] {
  return calculateLoadedDisks(
    getBarLoaderCapacityTotalWeight(unit),
    unit,
    optionalDisks,
    barWeight,
    collarsWeight,
  );
}

export function getBarLoaderWidthDivisor(barProps: BarLoaderProps): number {
  const unit = barProps.unit ?? "kg";
  const mode = barProps.mode ?? "full";
  const optionalDisks = barProps.optionalDisks;
  if (mode === "full") {
    return Math.max(1, getEnabledDisks(unit, optionalDisks).length);
  }
  if (mode === "small") {
    return Math.max(1, getCompactModeMaxDiskColumns(unit));
  }
  const maxLoaded = calculateLoadedDisksAtCapacity(
    unit,
    optionalDisks,
    barProps.barWeight,
    barProps.collarsWeight,
  );
  return Math.max(1, maxLoaded.length);
}

export function isBarLoaderCompactVariableWidth(
  barProps: BarLoaderProps,
): boolean {
  const mode = barProps.mode ?? "full";
  return (
    (mode === "small" || mode === "bar") &&
    (barProps.compactWidthMode ?? "fixed") === "variable"
  );
}

export function getDefaultDiskStyles(
  unit: BarLoaderUnit,
): Record<string, BarLoaderDiskStyleEntry> {
  const entries: Record<string, BarLoaderDiskStyleEntry> = {};
  for (const disk of BAR_LOADER_DISKS_BY_UNIT[unit]) {
    entries[String(disk.weight)] = {
      labelText: String(disk.weight),
      label: { ...DEFAULT_BAR_LOADER_LABEL_STYLE },
      disk: {
        ...DEFAULT_BAR_LOADER_DISK_STYLE,
        backgroundColor: disk.defaultColor,
      },
    };
  }
  return entries;
}

/**
 * Default props for a newly inserted bar-loader component node (palette / toolbar).
 * Ensures `diskDefaultStyles` exists so resize sync (`syncBarLoaderDiskStylesToNodeBounds`)
 * updates global disk dimensions together with per-disk overrides.
 *
 * @returns Props aligned with unit-switch handling in `BarLoaderFields` (same default unit `kg`).
 */
export function createInitialBarLoaderProps(): BarLoaderProps {
  const unit: BarLoaderUnit = "kg";
  const diskDefaults = getDefaultDiskStyles(unit);
  return {
    totalWeight: 125,
    barWeight: BAR_WEIGHT_BY_UNIT[unit],
    collarsWeight: DEFAULT_COLLARS_WEIGHT,
    mode: "full",
    unit,
    diskStyles: diskDefaults,
    diskDefaultStyles: diskDefaults,
  };
}

function toNumberPx(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value.replace("px", "").trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

/**
 * Horizontal and vertical spacing (margin + padding) for a bar-loader block.
 */
export function getBarLoaderBlockInsets(
  style: BarLoaderBlockStyle | undefined,
): {
  horizontal: number;
  vertical: number;
} {
  const marginBase = toNumberPx(style?.margin, 0);
  const paddingBase = toNumberPx(style?.padding, 0);
  const marginLeft = toNumberPx(style?.marginLeft, marginBase);
  const marginRight = toNumberPx(style?.marginRight, marginBase);
  const marginTop = toNumberPx(style?.marginTop, marginBase);
  const marginBottom = toNumberPx(style?.marginBottom, marginBase);
  const paddingLeft = toNumberPx(style?.paddingLeft, paddingBase);
  const paddingRight = toNumberPx(style?.paddingRight, paddingBase);
  const paddingTop = toNumberPx(style?.paddingTop, paddingBase);
  const paddingBottom = toNumberPx(style?.paddingBottom, paddingBase);
  return {
    horizontal: marginLeft + marginRight + paddingLeft + paddingRight,
    vertical: marginTop + marginBottom + paddingTop + paddingBottom,
  };
}

/**
 * Minimum document-space width/height for the bar loader so outer boxes (incl. margin/padding)
 * match {@link LayoutBarLoader} flex layout. Used for auto-resize in the editor and for
 * distributing extra node space without double-counting spacing.
 */
export function getBarLoaderFootprint(
  barProps: BarLoaderProps,
  styles: Record<string, BarLoaderDiskStyleEntry>,
): { width: number; height: number } {
  const unit = barProps.unit ?? "kg";
  const mode = barProps.mode ?? "full";
  const totalWeight = Number(barProps.totalWeight ?? 0);
  const enabledDisks = getEnabledDisks(unit, barProps.optionalDisks);
  const loadedDisks = calculateLoadedDisks(
    totalWeight,
    unit,
    barProps.optionalDisks,
    barProps.barWeight,
    barProps.collarsWeight,
  );
  const getCount = (weight: number) =>
    loadedDisks.filter((value) => value === weight).length;
  const isVariableCompactWidth = isBarLoaderCompactVariableWidth(barProps);

  const getLabelOuter = (weight: number): { width: number; height: number } => {
    const label = styles[String(weight)]?.label;
    const insets = getBarLoaderBlockInsets(label);
    return {
      width: Math.max(0, toNumberPx(label?.width, 60) + insets.horizontal),
      height: Math.max(0, toNumberPx(label?.height, 56) + insets.vertical),
    };
  };

  const getDiskOuter = (weight: number): { width: number; height: number } => {
    const disk = styles[String(weight)]?.disk;
    const insets = getBarLoaderBlockInsets(disk);
    return {
      width: Math.max(0, toNumberPx(disk?.width, 60) + insets.horizontal),
      height: Math.max(0, toNumberPx(disk?.height, 112) + insets.vertical),
    };
  };

  if (mode === "bar") {
    if (isVariableCompactWidth) {
      const width = loadedDisks.reduce(
        (acc, w) => acc + getDiskOuter(w).width,
        0,
      );
      const height = loadedDisks.reduce(
        (acc, w) => Math.max(acc, getDiskOuter(w).height),
        0,
      );
      return { width, height };
    }
    const maxLoaded = calculateLoadedDisksAtCapacity(
      unit,
      barProps.optionalDisks,
      barProps.barWeight,
      barProps.collarsWeight,
    );
    const widthSum = maxLoaded.reduce(
      (acc, w) => acc + getDiskOuter(w).width,
      0,
    );
    const fallbackOuter =
      enabledDisks[0] !== undefined
        ? getDiskOuter(enabledDisks[0].weight).width
        : toNumberPx(undefined, 60);
    const width = widthSum > 0 ? widthSum : fallbackOuter;
    const height = enabledDisks.reduce(
      (acc, disk) => Math.max(acc, getDiskOuter(disk.weight).height),
      0,
    );
    return { width, height };
  }

  if (mode === "small") {
    if (isVariableCompactWidth) {
      const visibleDisks = enabledDisks.filter(
        (disk) => getCount(disk.weight) > 0,
      );
      const width = visibleDisks.reduce(
        (acc, disk) => acc + getDiskOuter(disk.weight).width,
        0,
      );
      const height = visibleDisks.reduce(
        (acc, disk) => Math.max(acc, getDiskOuter(disk.weight).height),
        0,
      );
      return { width, height };
    }
    const maxCols = Math.max(1, getCompactModeMaxDiskColumns(unit));
    const sumOuterW = enabledDisks.reduce(
      (acc, disk) => acc + getDiskOuter(disk.weight).width,
      0,
    );
    const avgOuterW =
      enabledDisks.length > 0
        ? sumOuterW / enabledDisks.length
        : toNumberPx(undefined, 60);
    const width = maxCols * avgOuterW;
    const height = enabledDisks.reduce(
      (acc, disk) => Math.max(acc, getDiskOuter(disk.weight).height),
      0,
    );
    return { width, height };
  }

  const columns = enabledDisks.map((disk) => {
    const label = getLabelOuter(disk.weight);
    const diskOuter = getDiskOuter(disk.weight);
    const hasDisk = getCount(disk.weight) > 0;
    return {
      width: hasDisk ? Math.max(label.width, diskOuter.width) : label.width,
      height: label.height + (hasDisk ? diskOuter.height : 0),
    };
  });

  return {
    width: columns.reduce((acc, column) => acc + column.width, 0),
    height: columns.reduce((acc, column) => Math.max(acc, column.height), 0),
  };
}

function applyBarLoaderLayoutIncrementsToEntries(
  merged: Record<string, BarLoaderDiskStyleEntry>,
  widthInc: number,
  lhInc: number,
  dhInc: number,
): Record<string, BarLoaderDiskStyleEntry> {
  const next: Record<string, BarLoaderDiskStyleEntry> = {};
  for (const key of Object.keys(merged)) {
    const entry = merged[key];
    if (!entry) continue;
    const lw = toNumberPx(entry.label?.width, 60);
    const lh = toNumberPx(entry.label?.height, 56);
    const dw = toNumberPx(entry.disk?.width, 60);
    const dh = toNumberPx(entry.disk?.height, 112);
    next[key] = {
      ...entry,
      label: {
        ...entry.label,
        width: Math.max(0, Math.round(lw + widthInc)),
        height: Math.max(0, Math.round(lh + lhInc)),
      },
      disk: {
        ...entry.disk,
        width: Math.max(0, Math.round(dw + widthInc)),
        height: Math.max(0, Math.round(dh + dhInc)),
      },
    };
  }
  return next;
}

export interface SyncBarLoaderDiskStylesResult {
  diskStyles: Record<string, BarLoaderDiskStyleEntry>;
  /** Present when `props.diskDefaultStyles` was defined (e.g. editor « All » / global defaults). */
  diskDefaultStyles?: Record<string, BarLoaderDiskStyleEntry>;
}

/**
 * After a manual node resize, applies width/height increments to every disk/label block
 * so stored styles match the same rules as {@link LayoutBarLoader} (footprint + shared delta / divisor).
 * Layout dimensions are rounded to integers. When global defaults exist, they receive the same deltas.
 */
export function syncBarLoaderDiskStylesToNodeBounds(
  props: BarLoaderProps,
  diskStyles: Record<string, BarLoaderDiskStyleEntry> | undefined,
  nodeWidth: number,
  nodeHeight: number,
): SyncBarLoaderDiskStylesResult {
  const unit = props.unit ?? "kg";
  const mode = props.mode ?? "full";
  const merged: Record<string, BarLoaderDiskStyleEntry> = {
    ...getDefaultDiskStyles(unit),
    ...(diskStyles ?? {}),
  };
  const fp = getBarLoaderFootprint(
    { ...props, totalWeight: props.totalWeight ?? 0 },
    merged,
  );
  const divisor = getBarLoaderWidthDivisor(props);
  const widthDelta = isBarLoaderCompactVariableWidth(props)
    ? 0
    : nodeWidth - fp.width;
  const widthInc = widthDelta / divisor;
  const heightDelta = nodeHeight - fp.height;
  const lhInc = mode === "full" ? heightDelta / 2 : 0;
  const dhInc = mode === "full" ? heightDelta / 2 : heightDelta;

  const nextDiskStyles = applyBarLoaderLayoutIncrementsToEntries(
    merged,
    widthInc,
    lhInc,
    dhInc,
  );

  if (props.diskDefaultStyles === undefined) {
    return { diskStyles: nextDiskStyles };
  }

  const mergedDefaults: Record<string, BarLoaderDiskStyleEntry> = {
    ...getDefaultDiskStyles(unit),
    ...props.diskDefaultStyles,
  };
  const nextDefaults = applyBarLoaderLayoutIncrementsToEntries(
    mergedDefaults,
    widthInc,
    lhInc,
    dhInc,
  );

  return { diskStyles: nextDiskStyles, diskDefaultStyles: nextDefaults };
}

/**
 * Applies a runtime behavior patch to bar-loader disk/label styles.
 * Mirrors {@link LayoutBarLoader} so footprint sizing stays consistent in the editor.
 */
export function applyBarLoaderTargetPatch(
  diskStyles: Record<string, BarLoaderDiskStyleEntry>,
  patch: BarLoaderTargetPatch,
  diskDefaultStyles: Record<string, BarLoaderDiskStyleEntry>,
): void {
  const applyToEntry = (
    entry: BarLoaderDiskStyleEntry,
    defaultEntry: BarLoaderDiskStyleEntry | undefined,
    allTarget: boolean,
  ) => {
    const canApply = (
      _currentValue: unknown,
      _defaultValue: unknown,
    ): boolean => (allTarget ? true : Object.is(_currentValue, _defaultValue));

    if (patch.key === "text" && patch.target.part === "label") {
      if (canApply(entry.labelText, defaultEntry?.labelText)) {
        entry.labelText = patch.value == null ? "" : String(patch.value);
      }
      return;
    }
    const targetStyle =
      patch.target.part === "label" ? entry.label : entry.disk;
    const defaultStyle =
      patch.target.part === "label" ? defaultEntry?.label : defaultEntry?.disk;
    if (patch.key === "visibility") {
      if (canApply(targetStyle.visibility, defaultStyle?.visibility)) {
        targetStyle.visibility =
          patch.value === true || patch.value === "true" || patch.value === 1;
      }
      return;
    }
    if (patch.key === "opacity") {
      const next = Number(patch.value);
      if (canApply(targetStyle.opacity, defaultStyle?.opacity)) {
        targetStyle.opacity = Number.isFinite(next) ? next : 1;
      }
      return;
    }
    if (patch.key === "fontSize") {
      const next = Number(patch.value);
      if (canApply(targetStyle.fontSize, defaultStyle?.fontSize)) {
        targetStyle.fontSize = Number.isFinite(next)
          ? next
          : targetStyle.fontSize;
      }
      return;
    }
    if (patch.key === "textAlign") {
      const next = String(patch.value ?? "").trim();
      if (
        (next === "left" || next === "center" || next === "right") &&
        canApply(targetStyle.textAlign, defaultStyle?.textAlign)
      ) {
        targetStyle.textAlign = next;
      }
      return;
    }
    if (patch.key === "textTransform") {
      const next = String(patch.value ?? "")
        .trim()
        .toLowerCase();
      if (
        (next === "none" ||
          next === "capitalize" ||
          next === "uppercase" ||
          next === "lowercase") &&
        canApply(targetStyle.textTransform, defaultStyle?.textTransform)
      ) {
        targetStyle.textTransform =
          next as BarLoaderBlockStyle["textTransform"];
      }
      return;
    }
    if (patch.key === "whiteSpace") {
      const next = normalizeWhiteSpaceValue(patch.value);
      if (canApply(targetStyle.whiteSpace, defaultStyle?.whiteSpace)) {
        targetStyle.whiteSpace = next;
      }
      return;
    }
    if (patch.key === "textDecorationLine") {
      const next = String(patch.value ?? "").trim();
      if (
        canApply(
          targetStyle.textDecorationLine,
          defaultStyle?.textDecorationLine,
        )
      ) {
        targetStyle.textDecorationLine = next || "none";
      }
      return;
    }
    if (patch.key === "textDecorationColor") {
      if (
        canApply(
          targetStyle.textDecorationColor,
          defaultStyle?.textDecorationColor,
        )
      ) {
        targetStyle.textDecorationColor = String(patch.value ?? "");
      }
      return;
    }
    if (patch.key === "textDecorationStyle") {
      const next = String(patch.value ?? "")
        .trim()
        .toLowerCase();
      if (
        (next === "solid" ||
          next === "double" ||
          next === "dotted" ||
          next === "dashed" ||
          next === "wavy") &&
        canApply(
          targetStyle.textDecorationStyle,
          defaultStyle?.textDecorationStyle,
        )
      ) {
        targetStyle.textDecorationStyle = next;
      }
      return;
    }
    if (patch.key === "textDecorationThickness") {
      const next = Number(patch.value);
      if (
        canApply(
          targetStyle.textDecorationThickness,
          defaultStyle?.textDecorationThickness,
        )
      ) {
        targetStyle.textDecorationThickness = Number.isFinite(next)
          ? Math.max(0, next)
          : targetStyle.textDecorationThickness;
      }
      return;
    }
    if (patch.key === "fontFamily" || patch.key === "fontWeight") {
      if (canApply(targetStyle[patch.key], defaultStyle?.[patch.key])) {
        targetStyle[patch.key] = String(patch.value ?? "");
      }
      return;
    }
    if (
      patch.key === "backgroundColor" ||
      patch.key === "color" ||
      patch.key === "transform"
    ) {
      if (canApply(targetStyle[patch.key], defaultStyle?.[patch.key])) {
        targetStyle[patch.key] = String(patch.value ?? "");
      }
      return;
    }
    if (
      patch.key === "border" &&
      patch.value &&
      typeof patch.value === "object"
    ) {
      const borderValue = patch.value as {
        border?: { top: string; right: string; bottom: string; left: string };
      };
      if (borderValue.border) {
        if (canApply(targetStyle.borderTop, defaultStyle?.borderTop)) {
          targetStyle.borderTop = borderValue.border.top;
        }
        if (canApply(targetStyle.borderRight, defaultStyle?.borderRight)) {
          targetStyle.borderRight = borderValue.border.right;
        }
        if (canApply(targetStyle.borderBottom, defaultStyle?.borderBottom)) {
          targetStyle.borderBottom = borderValue.border.bottom;
        }
        if (canApply(targetStyle.borderLeft, defaultStyle?.borderLeft)) {
          targetStyle.borderLeft = borderValue.border.left;
        }
        if (canApply(targetStyle.border, defaultStyle?.border)) {
          targetStyle.border =
            borderValue.border.top === borderValue.border.right &&
            borderValue.border.right === borderValue.border.bottom &&
            borderValue.border.bottom === borderValue.border.left
              ? borderValue.border.top
              : undefined;
        }
      }
      return;
    }
    if (
      patch.key === "borderRadius" &&
      patch.value &&
      typeof patch.value === "object"
    ) {
      const radiusValue = patch.value as {
        radius?: {
          topLeft: string;
          topRight: string;
          bottomRight: string;
          bottomLeft: string;
        };
      };
      if (radiusValue.radius) {
        if (
          canApply(
            targetStyle.borderTopLeftRadius,
            defaultStyle?.borderTopLeftRadius,
          )
        ) {
          targetStyle.borderTopLeftRadius = radiusValue.radius.topLeft;
        }
        if (
          canApply(
            targetStyle.borderTopRightRadius,
            defaultStyle?.borderTopRightRadius,
          )
        ) {
          targetStyle.borderTopRightRadius = radiusValue.radius.topRight;
        }
        if (
          canApply(
            targetStyle.borderBottomRightRadius,
            defaultStyle?.borderBottomRightRadius,
          )
        ) {
          targetStyle.borderBottomRightRadius = radiusValue.radius.bottomRight;
        }
        if (
          canApply(
            targetStyle.borderBottomLeftRadius,
            defaultStyle?.borderBottomLeftRadius,
          )
        ) {
          targetStyle.borderBottomLeftRadius = radiusValue.radius.bottomLeft;
        }
        if (canApply(targetStyle.borderRadius, defaultStyle?.borderRadius)) {
          targetStyle.borderRadius =
            radiusValue.radius.topLeft === radiusValue.radius.topRight &&
            radiusValue.radius.topRight === radiusValue.radius.bottomRight &&
            radiusValue.radius.bottomRight === radiusValue.radius.bottomLeft
              ? radiusValue.radius.topLeft
              : undefined;
        }
      }
      return;
    }
    if (
      patch.key === "padding" &&
      patch.value &&
      typeof patch.value === "object"
    ) {
      const spacingValue = patch.value as {
        margin?: {
          sides?: { top: number; right: number; bottom: number; left: number };
        };
        padding?: {
          sides?: { top: number; right: number; bottom: number; left: number };
        };
      };
      const marginSides = spacingValue.margin?.sides;
      const paddingSides = spacingValue.padding?.sides;
      if (marginSides) {
        if (canApply(targetStyle.marginTop, defaultStyle?.marginTop)) {
          targetStyle.marginTop = `${marginSides.top}px`;
        }
        if (canApply(targetStyle.marginRight, defaultStyle?.marginRight)) {
          targetStyle.marginRight = `${marginSides.right}px`;
        }
        if (canApply(targetStyle.marginBottom, defaultStyle?.marginBottom)) {
          targetStyle.marginBottom = `${marginSides.bottom}px`;
        }
        if (canApply(targetStyle.marginLeft, defaultStyle?.marginLeft)) {
          targetStyle.marginLeft = `${marginSides.left}px`;
        }
      }
      if (paddingSides) {
        if (canApply(targetStyle.paddingTop, defaultStyle?.paddingTop)) {
          targetStyle.paddingTop = `${paddingSides.top}px`;
        }
        if (canApply(targetStyle.paddingRight, defaultStyle?.paddingRight)) {
          targetStyle.paddingRight = `${paddingSides.right}px`;
        }
        if (canApply(targetStyle.paddingBottom, defaultStyle?.paddingBottom)) {
          targetStyle.paddingBottom = `${paddingSides.bottom}px`;
        }
        if (canApply(targetStyle.paddingLeft, defaultStyle?.paddingLeft)) {
          targetStyle.paddingLeft = `${paddingSides.left}px`;
        }
      }
    }
  };

  if (patch.target.weight === "all") {
    for (const [weightKey, entry] of Object.entries(diskStyles)) {
      applyToEntry(entry, diskDefaultStyles[weightKey], true);
    }
    return;
  }

  const weightKey = String(patch.target.weight);
  const entry = diskStyles[weightKey];
  if (!entry) return;
  applyToEntry(entry, diskDefaultStyles[weightKey], false);
}

/** Disk styles after defaults merge and optional runtime target patches. */
export function buildBarLoaderDiskStylesForRender(
  props: BarLoaderProps,
  targetPatches?: BarLoaderTargetPatch[],
): Record<string, BarLoaderDiskStyleEntry> {
  const unit = props.unit ?? "kg";
  const diskStyles: Record<string, BarLoaderDiskStyleEntry> = {
    ...getDefaultDiskStyles(unit),
    ...(props.diskStyles ?? {}),
  };
  const diskDefaultStyles =
    props.diskDefaultStyles ?? getDefaultDiskStyles(unit);
  const patches = targetPatches ?? props.barLoaderTargetPatches ?? [];
  for (const patch of patches) {
    applyBarLoaderTargetPatch(diskStyles, patch, diskDefaultStyles);
  }
  return diskStyles;
}
